package nl.volleyplanner.api.planner

import kotlinx.datetime.LocalDate
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.LocalTime
import kotlinx.serialization.Serializable
import kotlinx.serialization.Transient
import nl.volleyplanner.api.sports.BasicPoule
import nl.volleyplanner.api.sports.BasicTeam
import nl.volleyplanner.api.types.PublicDataPayload
import nl.volleyplanner.api.types.TransformableToPublic
import nl.volleyplanner.extensions.toSlug

@Serializable
data class PlanningProblemPublic(
    val name: String,
    val slug: String
) : PublicDataPayload

@Serializable
data class PlanningProblem(
    val identifier: String,
    val name: String,
    val slug: String,
    val archived: Boolean,
    val published: Boolean,
    val startDate: LocalDate,
    val endDate: LocalDate,
    val plannerConfigurationMetadata: PlannerConstraintConfigurationMetadata
    // latest/active version
) : TransformableToPublic<PlanningProblemPublic> {
    override fun toPublic() = PlanningProblemPublic(name, slug)
}

enum class CreatePlanningProblemVersionBaseVersionType {
    Blank, Published, Version
}

@Serializable
data class UpdatePlanningProblemVersionRequest(
    val problemSlug: String,
    val problemVersionSlug: String?,
    val plannerConfigurationIdentifier: String
)

@Serializable
data class CreatePlanningProblemVersionRequest(
    val name: String,
    val plannerConfigurationIdentifier: String,
    val baseVersionType: CreatePlanningProblemVersionBaseVersionType,
    val baseVersionSlug: String?
)

@Serializable
data class PlanningProblemVersion(
    val identifier: String,
    val name: String,
    val slug: String,
    val plannerConfigurationMetadata: PlannerConstraintConfigurationMetadata,
    val lastPublishedMergeTimestamp: LocalDateTime
)

/**
 * Request to merge latest published changes to a version
 * Can only merge changes that have been made after the previous merge from published to the version
 */
@Serializable
data class MergePublishedToVersionRequest(
    /**
     * The task ids to merge, leave task ids out for changes that shouldn't get merged
     */
    val taskIdsToMerge: Set<String>
)

@Serializable
data class GroupConstraint(
    val groupIdentifier: String,
    val groupLabel: String,
    val groupType: String
)

@Serializable
data class Ruleset(
    val identifier: String,
    val name: String,
    val description: String,
    val priority: Int,
    val archived: Boolean,
    val refereeRequiredAmount: Int,
    val scoreKeeperRequiredAmount: Int,
    val primaryRefereeGroups: List<GroupConstraint>,
    val alternativeRefereeGroups: List<GroupConstraint>,
    val primaryScoreKeeperGroups: List<GroupConstraint>,
    val alternativeScoreKeeperGroups: List<GroupConstraint>
) : Comparable<Ruleset> {
    @Transient
    val refereeValid = refereeRequiredAmount == 0 || (primaryRefereeGroups + alternativeRefereeGroups).isNotEmpty()
    @Transient
    val scoreKeeperValid = scoreKeeperRequiredAmount == 0 || (primaryScoreKeeperGroups + alternativeScoreKeeperGroups).isNotEmpty()

    override fun compareTo(other: Ruleset): Int {
        return priority.compareTo(other.priority).takeIf { it != 0 } ?: name.compareTo(other.name)
    }
}

data class TeamConfiguration(
    val team: BasicTeam,
    val pouleConfigurations: List<TeamPouleConfiguration>
)

@Serializable
data class TeamPouleConfiguration(
    val key: String,
    val team: BasicTeam,
    val poule: BasicPoule,
    val plannerSupported: Boolean,
    val rulesetIdentifier: String?,
    val refereeRequiredAmount: Int?,
    val scoreKeeperRequiredAmount: Int?
)

@Serializable
data class OwnMatchTimeReservationConstraintConfiguration(
    val timeReservationBeforeMatchMinutes: Int?,
    val timeReservationAfterMatchMinutes: Int?,
    val disableTimeReservationOnSameField: Boolean,
)

@Serializable
data class AgeGroupConstraintConfiguration(
    val ageGroupIdentifier: String,
    val ageGroupTimeLimit: LocalTime?,
    val ageGroupMaxWaitTimeMinutes: Int?
)

@Serializable
enum class PlannerBalancingModel {
    /**
     * Initial model to balance workload
     * Based on amount of tasks only, assigning fictive tasks for volunteers
     * Flaw: (very) high penalties when exceeding 2 or 3 tasks. Only works in small scopes
     */
    FictiveWorkloadSpread,

    /**
     * Model introduced in January 2026
     * Balances tasks by the amount of games a person has. Scales with the amount of games
     */
    MatchProportionalTaskLoad
}

@Serializable
data class BalancingConstraintConfiguration(
    val balancingModel: PlannerBalancingModel,
    val scheduleTeamsAtSameTime: Boolean
)

@Serializable
data class PlannerConstraintConfigurationMetadata(
    val identifier: String,
    val name: String,
    val slug: String
)

@Serializable
data class PlannerConstraintConfiguration(
    val identifier: String,
    val name: String,
    val slug: String,
    val ownMatchTimeReservationConstraintConfiguration: OwnMatchTimeReservationConstraintConfiguration,
    val ageGroupConstraints: List<AgeGroupConstraintConfiguration>,
    val balancingConstraintConfiguration: BalancingConstraintConfiguration
)

@Serializable
data class CreatePlannerConstraintConfigurationRequest(
    val name: String,
    val ownMatchTimeReservationConstraintConfiguration: OwnMatchTimeReservationConstraintConfiguration,
    val ageGroupConstraints: List<AgeGroupConstraintConfiguration>,
    val balancingConstraintConfiguration: BalancingConstraintConfiguration
)